import { useEffect, useRef, useState } from "react";
import { GenericAppGlyph } from "./AppImage";

/** App icons use metadata images, or a neutral glyph. Runtime hosts are never fetched. */

/**
 * The palette, keyed by NAME.
 *
 * The server stores `"rose"`, never `#da0d5e`: half a gradient in the database
 * and half in this file would drift the first time the palette was tuned. An
 * index would have been worse — reordering this list would silently repaint
 * every App that had ever picked a colour.
 *
 * `ICON_COLORS` in `services/compute_instances/personalization.ts` is the
 * server's copy of these keys, and decides which ones may be stored.
 */
export const PALETTE = {
  rose: { from: "#ff6b8a", to: "#da0d5e" },
  sky: { from: "#7dd3fc", to: "#3b82f6" },
  green: { from: "#86efac", to: "#16a34a" },
  amber: { from: "#fcd34d", to: "#f59e0b" },
  violet: { from: "#c4b5fd", to: "#7c3aed" },
  teal: { from: "#5eead4", to: "#0d9488" },
  orange: { from: "#fdba74", to: "#ea580c" },
  magenta: { from: "#f0abfc", to: "#c026d3" },
} as const;

export type IconColor = keyof typeof PALETTE;

/** Stated, so the derived-colour default does not depend on object key order. */
export const ICON_COLORS: readonly IconColor[] = [
  "rose",
  "sky",
  "green",
  "amber",
  "violet",
  "teal",
  "orange",
  "magenta",
];

export function isIconColor(value: unknown): value is IconColor {
  return typeof value === "string" && value in PALETTE;
}

/** The colour an App has when nobody has chosen one. */
export function derivedColor(seed: string): IconColor {
  return ICON_COLORS[hash(seed) % ICON_COLORS.length];
}

/** FNV-1a. Small, stable, and not trying to be a hash function for security. */
function hash(seed: string): number {
  let value = 0x811c9dc5;
  for (let index = 0; index < seed.length; index += 1) {
    value ^= seed.charCodeAt(index);
    value = Math.imul(value, 0x01000193);
  }
  return value >>> 0;
}

/**
 * Icon URLs this page load already knows are broken.
 *
 * Keeps a remount (returning from Discover's expanded pane, say) from making
 * the same failing request again and briefly showing a broken image. An icon
 * that has LOADED is deliberately not remembered: whether its bytes are still
 * in the browser's HTTP cache is not a fact this module can honestly hold, so
 * the `<img>` is always attempted and simply paints on its own if it can.
 *
 * Memory-only and not persisted: it is a statement about this page load.
 */
const failedIcons = new Set<string>();

export function AppIcon({
  seed,
  size = 64,
  color,
  glyph,
  imageUrl,
  listingIconUrl,
  onImageStatus,
}: {
  name: string;
  /** Stable across renames, so an App keeps its colour when retitled. */
  seed: string;
  size?: number;
  /** What the owner picked. Absent means the colour derived from the seed. */
  color?: string | null;
  /** What the owner picked. Optional private personalization. */
  glyph?: string | null;
  /** An uploaded picture. Wins over both colour and glyph — somebody who
   *  chose a picture chose it INSTEAD of the drawn icon, not on top of it. */
  imageUrl?: string | null;
  /** The catalogue listing's icon, on the public asset host. Below the
   *  owner's own picture and above the drawn icon: it is what the App is
   *  actually called and shown as everywhere else, but it is not a choice
   *  this person made. */
  listingIconUrl?: string | null;
  onImageStatus?: (status: "loading" | "ready" | "invalid" | "failed") => void;
}) {
  // Which picture we are attempting, and whether it has already failed. An
  // icon host can 404 or go away; when it does the tile must fall through to
  // the drawn icon rather than leave a broken image in the grid.
  const source = imageUrl
    ? { url: imageUrl, credentialed: true }
    : listingIconUrl
      ? { url: listingIconUrl, credentialed: false }
      : null;
  const url = source?.url ?? null;
  const imageRef = useRef<HTMLImageElement>(null);
  const [failed, setFailed] = useState<boolean>(() =>
    url ? failedIcons.has(url) : false,
  );
  useEffect(() => {
    // A new URL is a new attempt — replacing a picture must not inherit the
    // previous one's failure.
    setFailed(url ? failedIcons.has(url) : false);
  }, [url]);
  useEffect(() => {
    const image = imageRef.current;
    // Cached images may load before this effect. Preserve their readiness.
    onImageStatus?.(url && failedIcons.has(url) ? "failed" : image?.complete && image.naturalWidth > 0
      ? image.naturalWidth === image.naturalHeight && image.naturalWidth >= 256 && image.naturalWidth <= 4096 ? "ready" : "invalid"
      : "loading");
  }, [url, onImageStatus]);

  // Small icons (rows, cards, search) share one 6px corner; a proportional
  // radius reads as a circle below ~32px. Larger tiles keep the squircle.
  const radius = size <= 32 ? "var(--ato-radius-icon-sm, 6px)" : Math.round(size * 0.28);
  const colors = PALETTE[isIconColor(color) ? color : derivedColor(seed)];
  const chosen = glyph?.trim();
  // Explicit private personalization remains a choice; neither absent nor
  // failed images ever fall back to letters derived from the app title.
  const label = !source && chosen ? chosen : null;
  const drawn = (
    <span
      className="app-icon"
      style={{
        width: size,
        height: size,
        // A squircle-ish radius. iOS uses a superellipse; a proportional
        // radius is close enough at these sizes and costs no path.
        borderRadius: radius,
        backgroundImage: label ? `linear-gradient(145deg, ${colors.from}, ${colors.to})` : undefined,
        backgroundColor: label ? undefined : "#eef0f4",
        color: label ? undefined : "#667085",
        // Code points, not UTF-16 units: an emoji is one glyph but two or
        // more units, and measuring in units would render it at the
        // two-letter size and clip it.
        fontSize: Math.round(size * (label && [...label].length > 1 ? 0.34 : 0.42)),
      }}
      aria-hidden
    >
      {label ?? <GenericAppGlyph size={Math.round(size * 0.5)} />}
    </span>
  );

  if (!source || failed) return drawn;

  /*
   * The picture, over the drawn icon rather than instead of it.
   *
   * A bare `<img>` paints nothing until its bytes arrive, so the drawn icon
   * costs no request and is already correct for this App: same square, same
   * radius, same position, always behind. The picture is opaque from the first
   * paint rather than faded in — if its bytes are in the browser's disk or
   * memory cache it appears immediately, and only a genuine cache miss shows
   * the fallback until the request lands. A previous `opacity: 0` until
   * `onLoad` made even a cached picture show the fallback first, on every full
   * reload. On error the picture is dropped and the drawn icon simply remains.
   */
  return (
    <span
      className="app-icon-stack"
      style={{ width: size, height: size, borderRadius: radius }}
    >
      {drawn}
      <img
        ref={imageRef}
        className="app-icon app-icon--image"
        // crossOrigin MUST be set before src, and React applies these in JSX
        // order. Safari/WebKit (bug 291793) starts a no-CORS request the moment
        // `src` is set and then a SECOND, CORS request when `crossOrigin`
        // arrives, which both doubles the fetch and puts a wrong variant in the
        // cache — so a reload re-fetches instead of reading its cached copy.
        // The personal icon endpoint is session-authorized, and an <img> sends
        // no cookies cross-origin without this; a catalogue icon is already
        // public and asking for credentials there would fail CORS instead of
        // loading, so it sets no crossOrigin at all.
        crossOrigin={source.credentialed ? "use-credentials" : undefined}
        src={source.url}
        alt=""
        width={size}
        height={size}
        // A hold on an icon must reach the tile's own menu, not the browser's
        // "Save Image". The CSS covers the callout; this covers the drag.
        draggable={false}
        onError={() => {
          failedIcons.add(source.url);
          setFailed(true);
          onImageStatus?.("failed");
        }}
        onLoad={(event) => {
          const { naturalWidth: width, naturalHeight: height } = event.currentTarget;
          onImageStatus?.(width === height && width >= 256 && width <= 4096 ? "ready" : "invalid");
        }}
        style={{
          width: size,
          height: size,
          borderRadius: radius,
          // A catalogue icon is somebody else's artwork at an unknown aspect
          // ratio. Cover keeps the squircle full and the crop centred instead
          // of letting a wide image letterbox inside the grid.
          objectFit: "cover",
        }}
        aria-hidden
      />
    </span>
  );
}

/** Test seam: forget what this page load knows is broken. */
export function resetIconMemoryForTests(): void {
  failedIcons.clear();
}
